"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
Object.defineProperty(exports, "__esModule", { value: true });
var React = require("react");
var TypeScriptHelpers_1 = require("../utilities/TypeScriptHelpers");
/**
 * This hook performs an async operation and stores its result in component state. It protects
 * against the common pitfalls of trying to set state on an unmounted or stale component.
 *
 * This hook is okay for simple cases, but for more complex use cases, consider using a more robust
 * library such as react-query or apollo.
 *
 * The hook returns the following information:
 * - `data`: the most recent valid (successfully-fetched) data. Will be null before first fetch.
 *   _This will still contain the last valid data_ during subsequent loading states or when the most
 *   recent fetch returned an error, even though that data no longer corresponds to the current
 *   params.
 * - `loading`: a boolean that will be true while a fetch is in progress (either on initial load
 *   or during a refetch after the params change)
 * - `error`: if the most recent invocation of the fetch returned an error, it will be populated
 *   here.
 * - `refetch`: a function that can be used to trigger a refetch (for example, if the client
 *   made an update via other means). During the refetch, the old data/error will still be
 *   available.
 *
 * @param fetcher
 * @param params
 */
function useAsyncFetch(fetcher) {
    var params = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        params[_i - 1] = arguments[_i];
    }
    var _a = React.useState(0), refetchKey = _a[0], setRefetchKey = _a[1];
    var refetch = React.useCallback(function () { return setRefetchKey(function (old) { return old + 1; }); }, [setRefetchKey]);
    var _b = React.useReducer(asyncFetchReducer, { loading: true, data: null, error: undefined }), state = _b[0], dispatch = _b[1];
    React.useEffect(function () {
        // The effect will be initiated only when the component is under valid conditions (e.g. rendered).
        // Once the effect becomes stale (either via unmount or params change), `queryStatus` gets set
        // to `stale` in the cleanup. A stale query status signifies that the fetch result should be
        // discarded.
        var queryStatus = 'valid';
        function fetchTheData() {
            return __awaiter(this, void 0, void 0, function () {
                var data, e_1;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            dispatch({ type: 'START_FETCH' });
                            _a.label = 1;
                        case 1:
                            _a.trys.push([1, 3, , 4]);
                            return [4 /*yield*/, fetcher.apply(void 0, params)];
                        case 2:
                            data = _a.sent();
                            if (queryStatus === 'valid') {
                                dispatch({ type: 'FETCH_SUCCESSFUL', data: data });
                            }
                            return [3 /*break*/, 4];
                        case 3:
                            e_1 = _a.sent();
                            if (queryStatus === 'valid') {
                                dispatch({ type: 'FETCH_FAILED', error: e_1 });
                            }
                            return [3 /*break*/, 4];
                        case 4: return [2 /*return*/];
                    }
                });
            });
        }
        void fetchTheData();
        return function () {
            queryStatus = 'stale';
        };
    }, __spreadArrays([fetcher, refetchKey], params));
    return {
        loading: state.loading,
        data: state.data,
        error: state.error,
        refetch: refetch
    };
}
exports.useAsyncFetch = useAsyncFetch;
function asyncFetchReducer(prevState, action) {
    switch (action.type) {
        case 'START_FETCH': return {
            loading: true,
            data: prevState.data,
            error: prevState.error,
        };
        case 'FETCH_SUCCESSFUL': return {
            loading: false,
            data: action.data,
            error: undefined,
        };
        case 'FETCH_FAILED': return {
            loading: false,
            data: prevState.data,
            error: action.error,
        };
        default:
            TypeScriptHelpers_1.assertUnreachable(action);
            return prevState;
    }
}
//# sourceMappingURL=useAsyncFetch.js.map